import "react-quill-new/dist/quill.snow.css";
import "../../../highlight";
import "./quill/Quill.scss";

import clsx from "clsx";
import { debounce, forEach, get, isEqual } from "lodash-es";
import Delta from "quill-delta";
import QuillImageDropAndPaste, { type ImageData } from "quill-image-drop-and-paste";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, {
  type DeltaStatic,
  type EmitterSource,
  Quill,
  type RangeStatic,
} from "react-quill-new";
import type { ToolbarProps } from "react-quill-new/node_modules/quill/modules/toolbar";
import type { Doc, Op } from "sharedb/lib/client";
import { twMerge } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import type { PresenceDataType, RichTextOpType } from "../../../../shared/types/sharedb";
import type {
  OEmbedDataType,
  PathType,
  RichTextCellType,
  WorksheetType,
} from "../../../../shared/types/worksheet";
import { createId } from "../../../../shared/utils";
import { BLOCKS_PATH } from "../../../../shared/worksheets/constants";
import { createBlockOfType } from "../../../../shared/worksheets/doc-helpers";
import { showOEmbedModal } from "../Embed/EmbedModal";

import { CharacterCounter } from "./quill/CharacterCounter";
import { QuillCursors } from "./quill/QuillCursors";
import { ToggleableImageResize } from "./quill/ToggleableImageResize";
import { CustomLinkSanitizer } from "./quill/link-sanitizer";
import { maybePatchQuillCompositionAPI } from "./quill/quill-composition-patch";

import { collaborationUploadFile } from "api/api-worksheets";
import { getPresenceViewTypeFromPageTabVariant } from "components/materials/page/helpers";
import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { t } from "i18n/i18n";
import { DocViewContext } from "providers/DocViewProvider";
import { useAppSelector } from "store/index";
import {
  selectCurrentUserId,
  selectSelectedPageTabVariant,
  selectUsersInCurrentSection,
} from "store/selectors";
import { toastLocalizedOperationCatcher, toastLocalizedOperationError } from "utils/alerts";
import { USER_INPUT_EVENT_CATEGORY, addTrackingToEvent, getPageFieldProps } from "utils/amplitude";
import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";
import { fetchOEmbed } from "utils/oembed";
import { getUserFullName } from "utils/user-utils";
import { getUserColor } from "worksheets/helpers/colors";

Quill.register("modules/imageResize", ToggleableImageResize);
Quill.register("modules/imageDropAndPaste", QuillImageDropAndPaste);

Quill.register("modules/cursors", QuillCursors);
// This is commented out until we port the jupyter upload feature for testing
// Quill.register(LargeFormula);
// TODO Confirm and consolidate?
// Repeated code block below from previous authoring RichText code
Quill.register(CustomLinkSanitizer, true);

// list from https://github.com/quilljs/quill/issues/1108
// Add any attributes quill should handle here ex: "width", "height", "alt"
const formats = [
  "background",
  "bold",
  "color",
  "font",
  "code",
  "italic",
  "link",
  "size",
  "strike",
  "script",
  "underline",
  "blockquote",
  "header",
  "indent",
  "list",
  "align",
  "direction",
  "code-block",
  "formula",
  "image",
  // "width", // Had to comment this out because it wasn't compatible with Quill 2.0. What was it for?
  // avoid pasting videos which are naively inserted into ShareDB doc causing memory issues
  // TODO: Add a handler that uploads this to S3 and posts link inline,
  // or add a dedicated media block component
  // 'video'
];

// We use userIDs internally to track cursors, but when we receive a disconnect we only receive
// the ShareDB auto-generated session id. Therefore, we make sure to track the mapping between
// userId and ShareDB sessionId when we first set up a new cursor, so that we can use this later
// to track which cursor to remove on disconnect.
const IDMapping: Record<string, string> = {};

const IMAGE_RESIZE_OPS_MS_DEBOUNCE_INTERVAL_MS = 1000;

interface QuillRichTextFieldProps {
  className?: string;
  doc: Doc<WorksheetType>;
  path: PathType;
  readOnly?: boolean;
  embedded?: boolean;
  eventName?: string;
  characterCounter: "NONE" | "READ" | "EDIT";
  dataPathForCounter?: PathType;
  maxLength?: number | null;
  showPlaceholder?: boolean;
  placeHolderText?: string;
  offerEmbedOnPasteURL?: boolean;
  /**
   * Initialize the last part of the path, if the data at doc[<path>] doesn't exist.
   */
  create?: boolean;
}

// TODO: These come from quill-cursors.ts in @minervaproject/quill-cursors, we should export our own declaration file, update this library, or fix the library's errors.
interface IQuillRangeType {
  index: number;
  length: number;
}

interface QuillCursorsType {
  createCursor: (id: string, name: string, color: string) => void;
  flashCursor: (id: string) => void;
  moveCursor: (id: string, range: IQuillRangeType) => void;
  removeCursor: (id: string) => void;
}

// https://github.com/quilljs/quill/blob/develop/modules/toolbar.ts
interface ToolbarType {
  addHandler: (format: string, handler: () => void) => void;
}

const getTruncatedPasteFromDelta = (
  delta: DeltaStatic,
  maxLength: number,
  extendedLength: number,
) => {
  const exceededTextCount = extendedLength - maxLength;
  let numExceeded = 1;

  for (let i = delta.ops.length - 1; i > -1; i -= 1) {
    if ("insert" in delta.ops[i]) {
      const textLength = (delta.ops[i].insert as string).length;
      const newLength = numExceeded + textLength;

      if (newLength === exceededTextCount) {
        return new Delta({ ops: delta.ops.slice(0, i) });
      }

      if (newLength > exceededTextCount) {
        const newDelta = new Delta({ ops: delta.ops.slice(0, i + 1) });

        const allowedCount = newLength - exceededTextCount;
        const allowedText = (delta.ops[i].insert as string).substring(0, allowedCount);

        newDelta.ops[i].insert = allowedText;

        return newDelta;
      }

      numExceeded = newLength;
    }
  }

  return delta;
};

/**
 * Can be used to display a FreeResponseField, TextField or Table cell,
 * where TextField will show a static display unless in authoring mode.
 */
export const QuillRichTextField: FC<QuillRichTextFieldProps> = ({
  className = "",
  embedded = false,
  doc,
  path,
  maxLength = null,
  characterCounter = "NONE",
  dataPathForCounter,
  showPlaceholder = false,
  placeHolderText,
  create = false,
  eventName,
  readOnly: readOnlyProp,
  offerEmbedOnPasteURL = false,
}) => {
  const [renderKey, setRenderKey] = useState(0);
  const [editorInstance, setEditorInstance] = useState<InstanceType<typeof Quill>>(null);
  const [uploading, setUploading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [errorClass, setErrorClass] = useState("");
  const originalValue = get(doc.data, path) as DeltaStatic;
  const user_id = useAppSelector(selectCurrentUserId);
  const {
    showResponses,
    isAuthoringPage,
    readOnly: readOnlyCtx,
    pageId,
  } = useContext(DocViewContext);
  const users = useAppSelector(selectUsersInCurrentSection);
  const wrapperRef = useRef(null);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const presenceViewType = getPresenceViewTypeFromPageTabVariant(tabVariant);

  // If the caller passes readOnly as a prop, we use that; otherwise we use the context value.
  // This means that the prop can override the context, if necessary.
  const readOnly = typeof readOnlyProp === "boolean" ? readOnlyProp : readOnlyCtx;

  // We use a ref to cache the readOnly value specifically to avoid re-calculating callbacks used
  // in the quill modules, as changing quill modules during runtime will cause re-renders which,
  // while working fine, affects the page height temporarily and causes scroll jumping if part
  // of the editor is not in view.
  // Do not use these refs for anything else.
  const readOnlyRef = useRef(readOnly);
  useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  // We use this to avoid re-running the main useEffect when the `users` variable change, because
  // users change a lot and we're just using it as a secondary lookup for usernames.
  const usersRef = useRef(users);
  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  // This is a workaround to filter out known erroneous or corrupt operations client-side.
  // The backend can reject operations that are invalid, but for content that has existing issues,
  // this approach allows us to keep displaying it, and fix the content on the fly.
  // We can likely take this out after a transitionary period, once the backend is more robust
  // in rejecting invalid operations, e.g. starting March 2025.
  useEffect(() => {
    if (originalValue?.ops?.length && !readOnly) {
      const originalOps = originalValue.ops;
      // Collect indexes of ops that are either missing an "insert" key, or have a "retain" key
      const opIndexesToRemove = originalOps.reduce<number[]>((acc, op, index) => {
        if ([undefined, null].includes(op.insert) || op.retain) {
          acc.push(index);
        }
        return acc;
      }, []);

      if (opIndexesToRemove.length > 0) {
        logInfoWithDebugFlag(
          "debug_rte",
          "QuillRichTextField: filtering non-insert ops client-side",
          {
            originalOps: JSON.stringify(originalOps),
            filteredOps: JSON.stringify(
              originalOps.filter((_, index) => !opIndexesToRemove.includes(index)),
            ),
            opIndexesToRemove,
          },
        );

        // Submit operations to delete the invalid ops
        // Delete in reverse order (highest index first) to prevent index shifting issues
        // when multiple deletions happen sequentially
        const removeOps = opIndexesToRemove
          .sort((a, b) => b - a)
          .map((i) => ({
            p: [...path, "ops", i],
            ld: originalOps[i],
            ldCheck: true, // custom flag to enable safety check to prevent deleting wrong element
          }));
        doc.submitOp(removeOps, {}, (err) => {
          if (err) {
            console.error(err);
          }
        });

        // This forces a refresh of the editor, because the Quill component doesn't re-mount
        // on external value change
        setRenderKey((prev) => prev + 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalValue, path, readOnly]);

  const saveToServer = useCallback((editor: InstanceType<typeof Quill>, file: File) => {
    const data = new FormData();
    data.append("image", file);

    setUploading(true);
    collaborationUploadFile(data)
      .then((response) => {
        const selection = editor.getSelection();
        editor.insertEmbed(selection.index, "image", response.url, "user");
        editor.setSelection(selection.index + 1, selection.length);
        setUploading(false);
      })
      .catch((error) => {
        rollbarAndLogError(error);
        toastLocalizedOperationCatcher("image_upload_failure")(error);
        setUploading(false);
      });
  }, []);

  const showEmbedModal = useCallback(
    async (link: string) => {
      const addEmbedBlock = (data: OEmbedDataType) => {
        const blockIndexPart = path[1];
        const newBlockIndex =
          typeof blockIndexPart === "number"
            ? blockIndexPart + 1
            : parseInt(blockIndexPart, 10) + 1;
        const block = createBlockOfType("Embed");
        block.f[0] = { ...block.f[0], ...data };

        doc.submitOp([{ p: [BLOCKS_PATH, newBlockIndex], li: block }]);
      };

      // We only want to detect links added by the user
      if (link) {
        const { embed: oEmbedData } = await fetchOEmbed(link);
        if (oEmbedData) {
          showOEmbedModal(link, addEmbedBlock, true);
        }
      }
    },
    [doc, path],
  );

  // This is an adapter that allows us to use the QuillImageDropAndPaste module
  const imageDropToSave = useCallback(
    function _saveToServer(dataUrl: string, type_: string, pluginImageData: ImageData) {
      if (readOnlyRef.current) {
        return;
      }
      // eslint-disable-next-line react/no-this-in-sfc, @typescript-eslint/no-unsafe-member-access, babel/no-invalid-this,@typescript-eslint/no-unsafe-argument
      saveToServer(this.quill, pluginImageData.toFile(createId()));
    },

    [saveToServer],
  );

  const debouncedResizeSubmit = useMemo(
    () =>
      debounce((resizeOps: DeltaStatic["ops"]) => {
        const op = [{ p: path, t: "rich-text", o: resizeOps, u: user_id }];
        doc.submitOp(op, { source: editorInstance });
      }, IMAGE_RESIZE_OPS_MS_DEBOUNCE_INTERVAL_MS),
    [doc, user_id, path, editorInstance],
  );

  useEffect(() => {
    const editor = editorInstance;
    if (!editor) {
      return;
    }

    const onPresence = (srcPresenceId: string, presence: PresenceDataType) => {
      // We don't show presence on the projector view, because the scaling
      // interferes with the cursor placement, making it look buggy.
      if (isProjectorView()) {
        return;
      }
      const cursorModule = editor.getModule("cursors") as QuillCursorsType;
      if (!presence) {
        // Remove the cursor if the source is disconnected
        if (IDMapping[srcPresenceId]) {
          cursorModule.removeCursor(IDMapping[srcPresenceId]);
          delete IDMapping[srcPresenceId];
        }
        return;
      }

      const presenceUserId = presence.u;
      if (!isEqual(presence.p, path)) {
        // Remove the cursor if the source user is active in another field
        cursorModule.removeCursor(presenceUserId);
        return;
      }

      // We only display presence coming from the same view as the current tab,
      // with the following exceptions:
      // - On the class responses tab, you can see presence from everyone, generally, as students only have
      //   permission to view the class responses tab if they are allowed to see everyone's responses.
      // - Instructors editing the template can for BLOCKS_PATH (editing instructions, etc.) will show presence
      //   on the student draft too.
      const isClassResponses = presenceViewType === "c";
      const matchingPresenceView = presence.v === presenceViewType;
      const comesFromTemplate = presence.v === "t";
      const isDraftPage = presenceViewType === "d";
      if (!matchingPresenceView && !isClassResponses && !(comesFromTemplate && isDraftPage)) {
        return;
      }

      if (presence.t !== "rich-text" || !presence.s) {
        rollbarAndLogError("Got invalid presence for QuillRichTextField", {
          presence,
        });
        return;
      }

      const isNew = !IDMapping[srcPresenceId];
      IDMapping[srcPresenceId] = presenceUserId;

      if (presenceUserId === user_id || !presence.s.s || presence.s.s.length === 0) {
        return;
      }

      // Future investigation: Can QuillCursors support multiple selections?
      const sel = presence.s.s[0];

      // Use Math.abs because the sharedb presence type
      // supports reverse selections, but I don't think
      // Quill Cursors does.
      const len = Math.abs(sel[1] - sel[0]);
      const min = Math.min(sel[0], sel[1]);

      const username = getUserFullName(usersRef.current?.find((u) => u.id === presenceUserId));

      try {
        cursorModule.createCursor(presenceUserId, username, getUserColor(presenceUserId));
        cursorModule.moveCursor(presenceUserId, { index: min, length: len });
        if (isNew) {
          cursorModule.flashCursor(presenceUserId);
        }
      } catch (e) {
        rollbarAndLogError(
          "Error while updating cursors/selections",
          {
            presenceUserId,
            username,
            len,
            min,
            docId: doc.id,
            presence,
          },
          e,
        );
      }
    };

    const updateCursor = (range: RangeStatic, _, source: EmitterSource) => {
      // TODO: Performance problem here, this re-submits presences from other users,
      // or submits a presence because your cursor was moved by another user.
      if (!range || source !== "user" || !doc.data) {
        return;
      }

      doc.localPresence.submit({
        p: path,
        t: "rich-text",
        v: presenceViewType,
        // Note that presence.s.u and presence.u are the same for all of _our_ use-cases, but
        // the assumption is that presence.u is the user sending the presence, and presence.s.u
        // is the user that the presence is about. We don't allow or need to send presence about
        // other users, so we should always use the same value for now.
        u: user_id,
        s: {
          u: user_id,
          s: [[range.index, range.index + range.length]],
        },
      });
    };

    const onOp = (ops: RichTextOpType[], source: InstanceType<typeof Quill>) => {
      // If the op is not for this path, we don't need to do anything
      if (!ops.some((op) => isEqual(op.p.slice(0, path.length), path))) return null;

      // If the op is from this editor, we don't need to do anything
      if (source === editor) return null;

      forEach(ops, (operation) => {
        // Ensures the ops are for exactly this editor in situations where there
        // are multiple active editors in the page
        if (isEqual(operation.p, path)) {
          editor.updateContents(operation.o);
        }
        if (operation.u !== user_id) {
          (editor.getModule("cursors") as QuillCursorsType).flashCursor(String(operation.u));
        }
      });
    };

    const selectLocalImage = () => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.style.display = "none";

      // Because we are generating the file input through the DOM and not through React,
      // we need to attach it to the body to ensure React does not remove it when
      // the component re-renders.
      document.body.appendChild(input);
      input.click();

      input.onchange = () => {
        const file = input.files[0];
        // file type is only image.
        if (/^image\//.test(file.type)) {
          saveToServer(editor, file);
        } else {
          toastLocalizedOperationError("can_only_upload_images");
        }

        document.body.removeChild(input);
      };
    };

    if (!readOnly) {
      editor.on("selection-change", updateCursor);
    }
    (editor.getModule("toolbar") as ToolbarType).addHandler("image", selectLocalImage);

    // Add listeners for doc collaboration
    doc.on("op", onOp);
    doc.presence.on("receive", onPresence);

    const handlePaste = (event: ClipboardEvent) => {
      const url = event.clipboardData.getData("text");
      if (url) {
        showEmbedModal(url);
      }
    };

    if (offerEmbedOnPasteURL) {
      editor.root.addEventListener("paste", handlePaste);
    }

    return () => {
      doc.removeListener("op", onOp);
      doc.presence.off("receive", onPresence);
      editor.off("selection-change", updateCursor);
      if (offerEmbedOnPasteURL) {
        editor.root.removeEventListener("paste", handlePaste);
      }
    };
  }, [
    readOnly,
    user_id,
    offerEmbedOnPasteURL,
    doc,
    editorInstance,
    path,
    doc.localPresence,
    saveToServer,
    showEmbedModal,
    presenceViewType,
  ]);

  // Timeline view replaces doc between renders, so we pull the new content here
  // Also, ShareDB's automatic error recovery will re-fetch the doc data entirely on error,
  // which this file needs to react to by detecting changes in doc.data.
  useEffect(() => {
    if (editorInstance) {
      const data = get(doc.data, path) as DeltaStatic;
      editorInstance.setContents(data);
    }
  }, [editorInstance, doc, path, doc?.data]);

  const handleOnChange = useCallback(
    (_, delta: DeltaStatic, source: EmitterSource) => {
      if (source !== "user") {
        return;
      }

      // first op is usually retain which we don't care about
      logInfoWithDebugFlag(
        "debug_rte",
        "User RTE change. First op:",
        JSON.stringify(delta?.ops?.[1] || delta?.ops?.[0] || "").substring(0, 50),
      );

      // Resize operations trigger very often and overwrite each other, so we debounce
      // these and submit only the last actions in a batch.
      const containsResizeOps = delta.ops.some((op) => !op.insert && op.attributes?.width);
      if (containsResizeOps) {
        debouncedResizeSubmit(delta.ops);
        return;
      }

      let finalDelta = delta;
      const edLength = editorInstance.getLength();

      // Quill documents require a newline at the end of the document, otherwise formatting will not be applied on
      // the last line (which will be noticed on page reload). See https://quilljs.com/docs/delta
      const lastChar = editorInstance.getText(edLength - 2, 1);
      if (lastChar !== "\n") {
        const newlineDelta = editorInstance.insertText(edLength - 1, "\n");
        finalDelta = finalDelta.compose(newlineDelta);
      }

      // We enforce maxLength by returning to the previous version if they exceed the max length.
      if (maxLength && edLength - 1 > maxLength) {
        setErrorClass("limit-exceeded");

        let position = editorInstance.getSelection().index;
        const oldDelta = new Delta(get(doc.data, path) as DeltaStatic);
        editorInstance.setContents(oldDelta);
        let updatedContent = false;

        // If we paste in text we only want to allow chars up to the maximum length
        if (edLength - 2 > maxLength && delta.ops.length > 1) {
          const retainPosition = "retain" in delta.ops[0] ? (delta.ops[0].retain as number) : 0;

          finalDelta = getTruncatedPasteFromDelta(delta, maxLength, edLength);

          const allowedCount = finalDelta.ops.reduce(
            (acc, op) => ("insert" in op ? acc + (op.insert as string).length : acc),
            0,
          );

          position = retainPosition + allowedCount + 1;
          updatedContent = true;

          editorInstance.updateContents(finalDelta);
        }

        // We need to set the position to the end and setting it directly will get overwritten.  So we
        // use a simple timeout here to ensure the position is set after the update.
        setTimeout(() => editorInstance.setSelection(position - 1, 0), 0);

        if (!updatedContent) {
          return;
        }
      } else {
        setErrorClass("");
      }

      const ops: (Op | RichTextOpType)[] = [
        { p: path, t: "rich-text", o: finalDelta.ops, u: user_id },
      ];

      if (create) {
        // This code ensures that pointing a QuillRichTextField to an empty data structure initializes
        // the structure before adding content data to it. Right now, this is only used for cells
        // in TableFields.
        // Path is either e.g. ['d', 'g', '<fieldId>', 'cm', '<rowId, colId>', 'v']
        // or             e.g. ['b', '0', 'f', '1', 'v', 'cm', '<rowId, colId>', 'v']
        // Note that this assumes that `cm` is initialized, and will overwrite any existing data
        // in cm.<rowId, colId> if data exists there (e.g. `ro` is set), but "v" doesn't.
        const currentData = get(doc.data, path) as RichTextCellType;
        const pathToParent = path.slice(0, -1);

        if (!currentData) {
          // need to create the field, before we can modify it
          const lastPathPart = path[path.length - 1];
          ops.unshift({
            p: pathToParent,
            oi: { [lastPathPart]: {} },
          });
        }
      }

      doc.submitOp(ops, { source: editorInstance }, (err) => {
        if (err) {
          rollbarAndLogError("Error submitting QuillRichTextField op", err);
        }
      });

      // Check for potential embedded links
      if (offerEmbedOnPasteURL && path[0] === BLOCKS_PATH) {
        // We only want to detect links added by the user
        const linkOp = finalDelta.ops.find((op) => (op.retain || op.insert) && op.attributes?.link);
        if (linkOp) {
          const link = linkOp?.attributes?.link as string;
          showEmbedModal(link);
        }
      }
    },
    [
      doc,
      offerEmbedOnPasteURL,
      debouncedResizeSubmit,
      editorInstance,
      maxLength,
      path,
      user_id,
      create,
      showEmbedModal,
    ],
  );

  // If the Quill modules change during a re-render, it breaks Quill completely.
  // So we enable _all_ possible toolbars we could need, and disable them via
  // CSS as needed.
  const modules = useMemo(() => {
    const toolbarOptions = {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "link", "blockquote"],
        ["image", "code-block", "formula"],
        [{ align: "" }, { align: "center" }, { align: "right" }], // , { align: "justify" }],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
          { color: [] },
        ],
      ],
      handlers: {},
    } as ToolbarProps;

    return {
      cursors: true,
      toolbar: toolbarOptions,
      // This line requires Quill to find a peer dependency "highlight.js", which we
      // inject into the window object in the highlight.js file.
      syntax: true,
      imageResize: { modules: ["Resize"] },
      // Functions need to be references directly, and can't be anonymous inside the module
      // config, otherwise Quill goes into a crash loop
      imageDropAndPaste: { handler: imageDropToSave },
      // We don't want edits by other users to be stored in the history and potentially be undone
      // by someone else.
      history: {
        userOnly: true,
      },
      // We disable the native image/video uploader to prevent it from duplicating functionality
      // we get through the `imageDropAndPaste` module. This fixes a bug where the image is uploaded
      // twice if drag & dropped into the editor.
      // This fix only became necessary recently, and we assume it's due to a Quill update adding the native
      // functionality. We can consider removing the `imageDropAndPaste` module if the native version
      // works just as well. For now, we didn't spend the time to investigate this.
      uploader: { handler: () => {} },
      // When the Quill component is embedded in a larger component (like a table), we don't want
      // to allow the user to enter a tab character; instead, pressing the tab key should change focus,
      // as it normally does in a browser. To do that, we need to disable the special handling that
      // Quill provides for the tab key.
      ...(embedded ? { keyboard: { bindings: { tab: false } } } : {}),
    };
  }, [imageDropToSave, embedded]);

  useEffect(() => {
    if (editorInstance) {
      const imageResize = editorInstance.getModule("imageResize") as ToggleableImageResize;
      imageResize.setEnabled(isAuthoringPage);
    }
  }, [editorInstance, isAuthoringPage]);

  // Quill breaks if its modules are re-defined during render, so we force a refresh
  // of the editor by changing its key when the modules change. Primarily, this is to
  // allow hot-reloading Quill during development, as we don't expect this to change during
  // a prod lifecycle. Other props (like placeholder) just don't update unless we refresh quill.
  const computedQuillId = useMemo(() => {
    return createId();
    // The dependency list here is used not as a dependency, but as a way to force a refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, showPlaceholder, renderKey]);

  const computedClassNames = useMemo(() => {
    let baseClass = isFocused ? "focused " : "";
    if (readOnly) {
      baseClass += "rte-read-only";
    }

    if (embedded) {
      return clsx("toolbar-hidden", baseClass);
    }

    if (showResponses) {
      return clsx("body toolbar-hidden textfield-disabled", baseClass);
    }

    // TODO: Look into hiding toolbar if not focused for students
    // Note that quill applies the "quill" class to the editor container,
    // which is what we use to style the editor.
    // For instructors, we hide the toolbar when they are in read only mode.
    return clsx([
      { "toolbar-hidden": readOnly || !isFocused, "textfield-disabled": readOnly },
      className,
      baseClass,
    ]);
  }, [embedded, showResponses, className, readOnly, isFocused]);

  // TODO: It would be more accessible to use focus/blur instead of explicit click handlers,
  // but it didn't interact well with the Quill toolbar. (Peter) I tried putting the focus
  // events on the wrapper of the richtext field, but that didn't seem to work either.
  // The events thrown by `onBlur` seem indistinguishable between clicking on the toolbar and outside,
  // so that `e.target` wasn't helpful. I'm sure there must be a better solution.
  useOnClickOutside(wrapperRef, () => {
    setIsFocused(false);
    // This prevents the editor from stealing focus when receiving updates from other users
    if (editorInstance?.hasFocus()) {
      editorInstance?.blur();
    }
  });

  return (
    <div
      className={clsx(errorClass, { "h-full": embedded })}
      key={computedQuillId}
      ref={wrapperRef}
      // Prevent automatic translation of Quill fields, as it wipes content that's not directly translatable
      translate="no"
      onClick={addTrackingToEvent({
        eventName: eventName || "Free response block selected",
        eventProperties: { ...getPageFieldProps(pageId, path), ...USER_INPUT_EVENT_CATEGORY },
        onEvent: () => setIsFocused(true),
      })}
    >
      <ReactQuill
        className={computedClassNames}
        defaultValue={originalValue}
        formats={formats}
        id={path.join(".")}
        modules={modules}
        placeholder={
          showPlaceholder && !readOnly && (placeHolderText ?? t("worksheet.placeholder.content"))
        }
        readOnly={readOnly}
        ref={(editorComponent) => {
          if (editorComponent) {
            const _editor = editorComponent.getEditor();
            maybePatchQuillCompositionAPI(_editor);
            if (_editor !== editorInstance) {
              setEditorInstance(_editor);
            }
          }
        }}
        onChange={handleOnChange}
      />
      {characterCounter !== "NONE" && (
        <div className="rich-text-counter pb-2 pr-4">
          <CharacterCounter
            doc={doc}
            editor={editorInstance}
            maxLength={maxLength}
            path={dataPathForCounter}
            readOnly={characterCounter === "READ"}
          />
        </div>
      )}

      {!readOnly && (
        <div
          className={twMerge(
            "animate-fade-out pointer-events-none absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center",
            uploading && "animate-fade-out-trigger pointer-events-auto",
          )}
          style={{
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
        >
          {t("rich_text_field.uploading_image")}
        </div>
      )}
    </div>
  );
};
